/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Spin, FloatButton } from 'antd'
import { ArrowUpOutlined, CalendarOutlined } from '@ant-design/icons'
import { connect } from 'react-redux'
import {
  getGameStatusTypesBySportId,
  getAllGameTypes,
} from '@sportninja/common/selectors/types'
import { useMyScheduleContext } from 'src/context/MyScheduleContext'
import FilterBar from '../components/FilterBar'
import Text from 'src/components/ts-components/Text'
import { DayOfGames } from 'src/components/List/GameList'
import { Mobile } from 'src/components/Responsive'
import { formatCivilDay } from 'src/components/List/GameList/CalendarView/buildCalendarDays'
import EventListRow from 'src/features/event/components/EventListRow'
import { groupGamesByDay } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import { filterEvents } from '@sportninja/common/features/event/filterEvents'
import { mergeEventsIntoDayBuckets } from '@sportninja/common/features/event/mergeEventsIntoDayBuckets'
import { frontierOf } from '@sportninja/common/features/event/scheduleWatermark'
import { useScheduleStream } from '@sportninja/common/features/event/hooks/useScheduleStream'
import { useEventExistsFromToday } from '@sportninja/common/features/event/hooks/useEventExistsFromToday'
import { resolveDecisionEventQuery } from '@sportninja/common/features/event/resolveDecisionEventQuery'
import { listMembershipEventsPage } from '@sportninja/common/services/event'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { useMembershipEventStream } from '@sportninja/common/features/event/hooks/useMembershipEventStream'
import { resolveDefaultScheduleTab } from '@sportninja/common/features/event/resolveDefaultScheduleTab'
import dayjs from 'dayjs'
import { resolveEventDateWindow } from 'src/context/MyScheduleContext/resolveEventDateWindow'
import ScheduleCardSkeleton from 'src/components/ts-components/ScheduleCardSkeleton'
import EmptyState from 'src/components/ts-components/EmptyState'

const SCROLL_THRESHOLD = 500

/**
 * MyScheduleList component
 * Displays the user's schedule with filters and game list
 */
const MyScheduleList = ({ gameStatusTypes, gameTypes }) => {
  const {
    games,
    loadingGames,
    loadingDates,
    datesLoaded,
    filtersLoaded,
    gamesFetched,
    gamesPagination,
    fetchMoreGames,
    selectedSortOrder,
    selectedDateRange,
    selectedTimePeriod,
    scheduleQuery,
    dates,
    setSelectedTimePeriod,
    setPeriodResolved,
    entityFilters,
  } = useMyScheduleContext()

  // The SAME query object the games request sends — filters and date window
  // both. Events now come from `/events/membership`, which takes the same
  // params, so passing the shared object is what keeps the two lists bounded
  // identically.
  //
  // No period-change refetch effect any more: the date window is part of this
  // object and therefore part of the query key, so switching Past / Today /
  // Upcoming changes the key and react-query refetches on its own. The old
  // endpoints took no date params, which is why that effect used to be needed.
  const {
    events,
    frontier: eventsFrontier,
    isLoading: eventsLoading,
    isFetching: eventsFetching,
    fetchNextPage: fetchNextEvents,
  } = useMembershipEventStream({
    query: scheduleQuery,
    enabled: filtersLoaded,
  })

  // A SECOND query, feeding ONLY the opening-tab decision.
  //
  // The list query above is bounded by the selected period, so it cannot
  // answer "does this viewer have anything today or later?" — the question
  // that decides which period to open on. Mirrors the competition `GameList`.
  //
  // The window is EXPLICIT, not absent: `/events/membership` answers an
  // unwindowed request with an empty list, which would silently decide every
  // load onto PAST.
  const decisionQuery = useMemo(() => {
    // `entityFilters`, NOT `scheduleQuery` — the latter carries the SELECTED
    // period's window, which would put the period in this query's key and
    // refetch it on every tab switch (two `/events/membership` calls per
    // switch instead of one).
    //
    // `membership_type` is games-only; `/events/membership` does not take it,
    // and this calls the service directly rather than through a hook that
    // strips it.
    //
    // Day-quantized by `resolveDecisionEventQuery`, so this memo's value is
    // stable across renders — it is part of a query key.
    const { membership_type: _membershipType, ...filters } = entityFilters
    return { ...filters, ...resolveDecisionEventQuery() }
  }, [entityFilters])

  // ONE row, from the start of today: the earliest event still ahead settles
  // the tab outright, so there is nothing a second row could change. See
  // `useEventExistsFromToday` for why this cannot come off the list query.
  const {
    eventStartsAt: decisionEventStartsAt,
    isLoading: eventsForDecisionLoading,
  } = useEventExistsFromToday({
    queryKey: queryKeys.events.decision('membership', decisionQuery),
    fetchPage: () => listMembershipEventsPage(decisionQuery, 1),
    enabled: filtersLoaded,
  })

  // The opening-period decision, run once both inputs are in: `/games/dates`
  // has answered and the decision events query has settled.
  //
  // The context picks a period from GAMES alone, which is final when it lands
  // on TODAY or UPCOMING — games win outright. But a games-only PAST is merely
  // "no games ahead", not "nothing ahead": a viewer whose only upcoming
  // activity is an event was always dropped on Past.
  // `resolveDefaultScheduleTab` applies that same games-first rule and only
  // consults events for the tie-break.
  const periodDecidedRef = useRef(false)
  useEffect(() => {
    if (
      periodDecidedRef.current ||
      !datesLoaded ||
      !filtersLoaded ||
      eventsForDecisionLoading
    ) {
      return
    }
    periodDecidedRef.current = true
    // Only upgrades the context's own games-only PAST. If the period is
    // anything else by now — games said TODAY/UPCOMING, or the user picked
    // while events were still loading — it is left alone.
    if (selectedTimePeriod === 'PAST') {
      const gameDates = (dates ?? []).map((d) =>
        dayjs(Array.isArray(d) ? d[0] : d).format('YYYY-MM-DD')
      )
      // Already bounded to today-onward by the query, so no client-side
      // narrowing is needed — the rows that came back are the candidates.
      const decided = resolveDefaultScheduleTab({
        gameDates,
        eventStartsAt: decisionEventStartsAt,
      }).toUpperCase()
      if (decided !== selectedTimePeriod) {
        setSelectedTimePeriod(decided)
      }
    }
    // Releases the games fetch, which is held until the period is final so a
    // games-only period and an upgraded one cannot race each other.
    setPeriodResolved()
    // `selectedTimePeriod` is read to avoid a redundant set, not to re-run
    // this — the ref already makes it once-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    datesLoaded,
    filtersLoaded,
    dates,
    decisionEventStartsAt,
    eventsForDecisionLoading,
    setSelectedTimePeriod,
    setPeriodResolved,
  ])

  const [isAppending, setIsAppending] = useState(false)
  const isAppendingRef = useRef(false)

  // Calculate if there are more pages to load
  const hasMore = Boolean(
    gamesPagination &&
      gamesPagination.current_page < gamesPagination.total_pages
  )

  // The games side of the merge, as a stream frontier. Games here come from the
  // context rather than an infinite query — `useScheduleStream` takes frontier
  // descriptors precisely so it does not care which.
  const gamesFrontier = {
    lastStartsAt: frontierOf(games, selectedSortOrder),
    hasMore,
    // While games are loading, `games` holds either nothing or the PREVIOUS
    // period's rows — neither is something the watermark can reason against.
    isPending: loadingGames,
  }

  // Load more games function
  const loadMoreGames = useCallback(async () => {
    if (loadingGames || isAppendingRef.current || !hasMore) {
      return
    }

    setIsAppending(true)
    isAppendingRef.current = true
    try {
      await fetchMoreGames()
    } catch (error) {
      console.error('🚨 Error loading more games:', error)
    } finally {
      setIsAppending(false)
      isAppendingRef.current = false
    }
  }, [loadingGames, hasMore, fetchMoreGames])

  // Events are keyed in their own timezone by `mergeEventsIntoDayBuckets`, so
  // games must be keyed venue-locally too (not viewer-local dayjs) or an
  // evening event lands a day away from the games it belongs with.
  const eventWindow = resolveEventDateWindow({
    dateRange: selectedDateRange,
    timePeriod: selectedTimePeriod,
  })
  // BOTH sides paginate now, so either can be the one that has not reached far
  // enough — the watermark holds back whichever rows sit beyond the nearer
  // frontier, games included. See `combineScheduleStreams`.
  const { visibleGames, visibleEvents, hasNextPage, fetchNextPage } =
    useScheduleStream({
      games,
      gamesFrontier,
      events: filterEvents(events, {
        startsAfter: eventWindow.startsAfter,
        startsBefore: eventWindow.startsBefore,
        order: selectedSortOrder,
      }),
      eventsFrontier,
      order: selectedSortOrder,
      fetchNextGames: loadMoreGames,
      fetchNextEvents,
      isFetching: loadingGames || isAppending || eventsFetching,
      // Filters and period both live in the games query the context builds;
      // changing either should let the stall guard try again.
      resetKey: JSON.stringify(scheduleQuery),
    })

  // Handle scroll event.
  //
  // Fetches whichever stream is holding the merge back, not games unconditionally
  // — advancing the other one would pull a page that changes nothing visible,
  // and the list would sit still while requests went out.
  // `fetchNextPage` is rebuilt every render; the listener below is attached once
  // per `loadingGames` change. A ref keeps the listener stable while still
  // calling the current one.
  const fetchNextPageRef = useRef(fetchNextPage)
  fetchNextPageRef.current = fetchNextPage

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && !loadingGames && !isAppendingRef.current) {
      fetchNextPageRef.current()
    }
  }, [loadingGames])

  // Attach scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Show loading while filters are being loaded
  if (!filtersLoaded) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
        `}
      >
        <Spin size='small' />
      </div>
    )
  }

  const dayBuckets = mergeEventsIntoDayBuckets(
    groupGamesByDay(visibleGames),
    visibleEvents,
    selectedSortOrder
  )

  const rowCount = dayBuckets.reduce(
    (total, bucket) => total + bucket.items.length,
    0
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        width: 100%;
      `}
    >
      {/* Filter Bar */}
      <FilterBar />

      {/* Separator Line */}
      <div
        css={css`
          height: 1px;
          background-color: ${colors.SECONDARY_200};
          margin-bottom: 16px;
        `}
      />

      {/* Content Area */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          min-width: 0;
          overflow: hidden;
        `}
      >
        {loadingGames ||
        loadingDates ||
        !datesLoaded ||
        !gamesFetched ||
        eventsLoading ? (
          // BOTH streams gate the skeleton. `eventsLoading` is the stream's
          // `isLoading` — true only with NO data yet, never on a background
          // refetch — so this cannot replace already-rendered games with a
          // skeleton when the 15s stale window lapses.
          //
          // It has to be here: the watermark admits nothing until each side has
          // loaded the period being asked about, so releasing on games alone
          // showed an empty list for as long as the events request took.
          //
          // `!gamesFetched` closes the gap between `datesLoaded` flipping true
          // and the fetch effect actually running. Effects run after paint, so
          // for one frame `loadingGames` is still false and `games` is still
          // its initial `[]` — which reads as "nothing scheduled" and flashes
          // the empty state between two skeletons.
          <ScheduleCardSkeleton />
        ) : rowCount === 0 ? (
          // Empty state
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 300px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
            `}
          >
            <EmptyState
              icon={<CalendarOutlined />}
              title='Nothing scheduled yet'
              subtitle={
                'Nothing is scheduled for this day.\nCheck upcoming or adjust your filters.'
              }
              // The card supplies the height; the default floor would push it
              // taller than the box.
              minHeight='auto'
            />
          </div>
        ) : (
          // Games list
          <>
            <Mobile>
              {(isMobile) => {
                return dayBuckets.map((bucket) => (
                  <DayOfGames
                    key={bucket.dayKey}
                    date={formatCivilDay(bucket.dayKey, 'cccc, LLL d, yyyy')}
                    // Games only — `DayOfGames` knows nothing about events.
                    // Event rows go through its `extraRows` slot instead, so
                    // they sit inside the same collapsible day section.
                    games={bucket.items
                      .filter((row) => row.kind === 'game')
                      .map((row) => row.game)}
                    extraRows={bucket.items
                      .filter((row) => row.kind === 'event')
                      .map((row, idx, rows) => (
                        <EventListRow
                          key={`event-${row.event.id}`}
                          event={row.event}
                          hasNextSibling={idx !== rows.length - 1}
                        />
                      ))}
                    gameStatusTypes={gameStatusTypes}
                    gameTypes={gameTypes}
                    isMobile={isMobile}
                    scheduleId={null}
                    isMySchedule={true}
                  />
                ))
              }}
            </Mobile>
            {/* Infinite scroll footer */}
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 24px 0;
                min-height: 60px;
              `}
            >
              {isAppending && (
                <div
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                  `}
                >
                  <Spin size='small' />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style='font-size: 14px; line-height: 1;'
                  >
                    Loading More...
                  </Text>
                </div>
              )}
              {!hasNextPage &&
                !loadingGames &&
                games.length > 0 &&
                gamesPagination &&
                gamesPagination.total_pages > 1 && (
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style='font-size: 14px;'
                  >
                    No More Results
                  </Text>
                )}
            </div>
          </>
        )}
      </div>
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    gameTypes: getAllGameTypes(state),
    gameStatusTypes: getGameStatusTypesBySportId(state, null),
  }
}

export default connect(mapStateToProps)(MyScheduleList)
